// hooks/useShipstationSelection.ts
import { useState, useEffect, useCallback } from "react";
import { Socket } from "socket.io-client";
import ISupplierModel from "@models/ISupplierModel"; // Adjust path
import IUserUI from "@models/IUserUI"; // Adjust path
import EndpointsEnum from "@models/EndpointsEnum"; // Adjust path

import IShipstationCarrierDTO from "@models/Shipstation/IShipstationCarrierDTO"; // Adjust path
import IShipstationServiceTypeDTO from "@models/Shipstation/IShipstationServiceTypeDTO"; // Adjust path
import IShipstationPackageTypeDTO from "@models/Shipstation/IShipstationPackageTypeDTO"; // Adjust path
import IGetShipstationCarriersDTO from "@models/DTO/IGetShipstationCarriersDTO"; // Adjust path
import IOnGetShipstationCarriersDTO from "@models/DTO/IOnGetShipstationCarriersDTO"; // Adjust path
import IGetShipstationServicesForCarrierDTO from "@models/DTO/IGetShipstationServicesForCarrierDTO"; // Adjust path
import IOnGetShipstationServicesForCarrierDTO from "@models/DTO/IOnGetShipstationServicesForCarrierDTO"; // Adjust path
import IGetShipstationPackageTypesForCarrierDTO from "@models/DTO/IGetShipstationPackageTypesForCarrierDTO"; // Adjust path
import IOnGetShipstationPackageTypesForCarrierDTO from "@models/DTO/IOnGetShipstationPackageTypesForCarrierDTO"; // Adjust path

interface UseShipstationSelectionProps {
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    // This hook needs to know when validation is complete and if there are orders to trigger the initial carrier fetch
    hasValidatedGuids: boolean;
    shippingOrdersLength: number; // Pass the length to avoid unnecessary re-renders if only content changes
}

const useShipstationSelection = ({
    socket,
    userModel,
    supplierModel,
    hasValidatedGuids,
    shippingOrdersLength,
}: UseShipstationSelectionProps) => {
    const [shipstationCarriers, setShipstationCarriers] = useState<IShipstationCarrierDTO[]>([]);
    const [shipstationServiceTypes, setShipstationServiceTypes] = useState<IShipstationServiceTypeDTO[]>([]);
    const [shipstationPackageTypes, setShipstationPackageTypes] = useState<IShipstationPackageTypeDTO[]>([]);
    const [activeGetCarriers, setActiveGetCarriers] = useState(false);
    const [activeGetServiceTypes, setActiveGetServiceTypes] = useState(false);
    const [activeGetPackageTypes, setActiveGetPackageTypes] = useState(false);
    const [shipstationCarrier, setShipstationCarrier] = useState("");
    const [shipstationServiceType, setShipstationServiceType] = useState("");
    const [shipstationPackageType, setShipstationPackageType] = useState("");

    // Define socket event handlers managed by this hook
    const onGetShipstationCarriers = useCallback((data: IOnGetShipstationCarriersDTO) => {
        setShipstationCarriers(data.carriers);
        setActiveGetCarriers(false);
    }, []); // Dependencies: Setters are stable

    const onGetShipstationServicesForCarrier = useCallback((data: IOnGetShipstationServicesForCarrierDTO) => {
        const shipstationServiceTypes = data.serviceTypes;
        setShipstationServiceTypes(shipstationServiceTypes);
        setActiveGetServiceTypes(false);
    }, []); // Dependencies: Setters are stable

    const onGetShipstationPackageTypesForCarrier = useCallback((data: IOnGetShipstationPackageTypesForCarrierDTO) => {
        setShipstationPackageTypes(data.packageTypes);
        setActiveGetPackageTypes(false);
    }, []); // Dependencies: Setters are stable

    // Fetch functions
    const getShipstationCarriers = useCallback(() => {
        if (!supplierModel.Supplier_Integration.has_shipstation) return;
        const data: IGetShipstationCarriersDTO = { userGuid: userModel.guid, userToken: userModel.token, supplierGuid: supplierModel.guid };
        socket.emit(EndpointsEnum.ShipstationGetCarriers, data);
        setActiveGetCarriers(true);
    }, [socket, userModel, supplierModel]); // Dependencies

    const getShipstationServicesForCarrier = useCallback(
        (carrierCode: string) => {
            if (!supplierModel.Supplier_Integration.has_shipstation || !carrierCode) return;
            const data: IGetShipstationServicesForCarrierDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                shipstation_carrierCode: carrierCode,
            };
            socket.emit(EndpointsEnum.ShipstationGetServicesForCarriers, data);
            setActiveGetServiceTypes(true);
        },
        [socket, userModel, supplierModel]
    ); // Dependencies

    const getShipstationPackageTypesForCarrier = useCallback(
        (carrierCode: string, serviceType: string) => {
            if (!supplierModel.Supplier_Integration.has_shipstation || !carrierCode || !serviceType) return;
            const data: IGetShipstationPackageTypesForCarrierDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                shipstation_carrierCode: carrierCode,
                shipstation_serviceType: serviceType,
            };
            socket.emit(EndpointsEnum.ShipstationGetPackageTypesForCarriers, data);
            setActiveGetPackageTypes(true);
        },
        [socket, userModel, supplierModel]
    ); // Dependencies

    // Effects to trigger fetches based on state changes

    // Trigger initial carrier fetch after validation completes
    useEffect(() => {
        if (hasValidatedGuids && shippingOrdersLength > 0 && supplierModel.Supplier_Integration.has_shipstation) {
            getShipstationCarriers();
        }
    }, [hasValidatedGuids, shippingOrdersLength, supplierModel.Supplier_Integration.has_shipstation, getShipstationCarriers]); // Dependencies

    // Trigger service fetch when carrier changes
    useEffect(() => {
        if (supplierModel.Supplier_Integration.has_shipstation && shipstationCarrier) {
            getShipstationServicesForCarrier(shipstationCarrier);
        } else if (supplierModel.Supplier_Integration.has_shipstation && !shipstationCarrier) {
            // If carrier is cleared, clear dependent states
            setShipstationServiceTypes([]);
            setShipstationServiceType("");
            setShipstationPackageTypes([]);
            setShipstationPackageType("");
        }
    }, [shipstationCarrier, supplierModel.Supplier_Integration.has_shipstation, getShipstationServicesForCarrier]); // Dependencies

    // Trigger package type fetch when service changes
    useEffect(() => {
        if (supplierModel.Supplier_Integration.has_shipstation && shipstationCarrier && shipstationServiceType) {
            getShipstationPackageTypesForCarrier(shipstationCarrier, shipstationServiceType);
        } else if (supplierModel.Supplier_Integration.has_shipstation && (!shipstationCarrier || !shipstationServiceType)) {
            // If carrier or service is cleared, clear dependent state
            setShipstationPackageTypes([]);
            setShipstationPackageType("");
        }
    }, [
        shipstationCarrier,
        shipstationServiceType,
        supplierModel.Supplier_Integration.has_shipstation,
        getShipstationPackageTypesForCarrier,
    ]); // Dependencies

    return {
        shipstationCarriers,
        shipstationServiceTypes,
        shipstationPackageTypes,
        shipstationCarrier,
        setShipstationCarrier, // Expose setter for handleChange
        shipstationServiceType,
        setShipstationServiceType, // Expose setter for handleChange
        shipstationPackageType,
        setShipstationPackageType, // Expose setter for handleChange
        activeGetCarriers,
        activeGetServiceTypes,
        activeGetPackageTypes,
        // Expose handlers needed by useSocketListeners
        onGetShipstationCarriers,
        onGetShipstationServicesForCarrier,
        onGetShipstationPackageTypesForCarrier,
    };
};

export default useShipstationSelection;
